/**
 * VAT Display Logic
 * 
 * Determines how VAT should be displayed to customers based on:
 * - Customer country
 * - Tax ID type
 * - VAT ID validity
 * 
 * German Tax Rules:
 * - German customers: Always show 19% MwSt
 * - EU B2B with valid VAT ID: Reverse-charge (no VAT)
 * - Non-EU: No VAT applicable
 */

/**
 * EU country codes
 */
export const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
];

/**
 * Tax ID types by region/country
 */
export const TAX_ID_TYPES = {
  // EU countries use EU_VAT
  EU: { 
    value: 'EU_VAT', 
    label: 'EU VAT ID', 
    placeholder: 'e.g., DE123456789',
    required: true,
    description: 'Required for reverse-charge'
  },
  // USA
  US: { 
    value: 'US_EIN', 
    label: 'US EIN (Employer ID)', 
    placeholder: 'e.g., 12-3456789',
    required: false,
    description: 'Optional for US businesses'
  },
  // Canada
  CA: { 
    value: 'CA_BN', 
    label: 'Business Number', 
    placeholder: '9 digits',
    required: false,
    description: 'Optional'
  },
  // United Kingdom (post-Brexit)
  GB: { 
    value: 'GB_VAT', 
    label: 'UK VAT Number', 
    placeholder: 'e.g., GB123456789',
    required: false,
    description: 'Optional for UK businesses'
  },
  // Switzerland
  CH: { 
    value: 'CH_MWST', 
    label: 'Swiss UID/MWST', 
    placeholder: 'e.g., CHE-123.456.789',
    required: false,
    description: 'Optional'
  },
  // Japan
  JP: { 
    value: 'JP_CORPORATE_NUMBER', 
    label: 'Corporate Number (法人番号)', 
    placeholder: '13 digits',
    required: false,
    description: 'Optional for Japanese businesses'
  },
  // South Korea
  KR: { 
    value: 'KR_BRN', 
    label: 'Business Registration Number', 
    placeholder: '10 digits',
    required: false,
    description: 'Optional'
  },
  // China
  CN: { 
    value: 'CN_USCC', 
    label: 'Unified Social Credit Code', 
    placeholder: '18 characters',
    required: false,
    description: 'Optional'
  },
  // Australia
  AU: { 
    value: 'AU_ABN', 
    label: 'Australian Business Number', 
    placeholder: '11 digits',
    required: false,
    description: 'Optional'
  },
  // Default for other countries
  OTHER: { 
    value: 'OTHER', 
    label: 'Tax ID / Business Number', 
    placeholder: 'Your business registration number',
    required: false,
    description: 'Optional'
  }
};

/**
 * Get the region code for Tax ID type determination
 * @param {string} countryCode - ISO 2-letter country code
 * @returns {string} Region key for TAX_ID_TYPES
 */
export function getRegion(countryCode) {
  if (!countryCode) return 'OTHER';
  const code = countryCode.toUpperCase();
  
  // Check if EU country
  if (EU_COUNTRIES.includes(code)) {
    return 'EU';
  }
  
  // Check for specific non-EU countries
  if (TAX_ID_TYPES[code]) {
    return code;
  }
  
  return 'OTHER';
}

/**
 * Get Tax ID type configuration for a country
 * @param {string} countryCode - ISO 2-letter country code
 * @returns {object} Tax ID type configuration
 */
export function getTaxIdType(countryCode) {
  const region = getRegion(countryCode);
  return TAX_ID_TYPES[region] || TAX_ID_TYPES.OTHER;
}

/**
 * Check if Tax ID is required for a country
 * @param {string} countryCode - ISO 2-letter country code
 * @returns {boolean}
 */
export function isTaxIdRequired(countryCode) {
  const taxIdType = getTaxIdType(countryCode);
  return taxIdType.required === true;
}

/**
 * Determines if VAT should be shown to the customer
 * @param {string} country - ISO 2-letter country code
 * @param {string} taxId - Customer's Tax ID (VAT ID for EU)
 * @param {boolean} taxIdValid - Whether the Tax ID has been validated
 * @returns {object} VAT display configuration
 */
export function getVATDisplayConfig(country, taxId = null, taxIdValid = false) {
  const code = country?.toUpperCase() || 'DE';
  
  // Germany: Always show 19% MwSt
  if (code === 'DE') {
    return {
      showVAT: true,
      vatRate: 19,
      vatLabel: 'zzgl. 19% MwSt.',
      vatLabelEN: 'plus 19% VAT',
      totalLabel: 'Gesamtpreis inkl. MwSt.',
      totalLabelEN: 'Total incl. VAT',
      note: null,
      category: 'domestic'
    };
  }
  
  // EU with valid VAT ID: Reverse-charge (no VAT shown)
  if (EU_COUNTRIES.includes(code) && taxId && taxIdValid) {
    return {
      showVAT: false,
      vatRate: 0,
      vatLabel: 'Reverse-charge',
      vatLabelEN: 'Reverse-charge',
      totalLabel: 'Gesamtpreis (Steuerschuldnerschaft)',
      totalLabelEN: 'Total (VAT reverse-charge)',
      note: 'Reverse-charge: VAT is due by the recipient (§13b UStG)',
      noteDE: 'Steuerschuldnerschaft des Leistungsempfängers (§13b UStG)',
      category: 'eu_reverse_charge'
    };
  }
  
  // EU without valid VAT ID: Prompt to provide VAT ID
  if (EU_COUNTRIES.includes(code) && (!taxId || !taxIdValid)) {
    return {
      showVAT: true,
      vatRate: 19,
      vatLabel: 'zzgl. 19% MwSt.',
      vatLabelEN: 'plus 19% VAT',
      totalLabel: 'Gesamtpreis inkl. MwSt.',
      totalLabelEN: 'Total incl. VAT',
      note: 'Please provide a valid VAT ID for reverse-charge (0% VAT)',
      noteDE: 'Bitte geben Sie eine gültige USt-IdNr. für Reverse-Charge an',
      category: 'eu_no_vat_id'
    };
  }
  
  // Non-EU: No VAT applicable
  return {
    showVAT: false,
    vatRate: 0,
    vatLabel: 'Keine MwSt.',
    vatLabelEN: 'No VAT',
    totalLabel: 'Gesamtpreis',
    totalLabelEN: 'Total',
    note: 'No VAT applicable (non-EU)',
    noteDE: 'Keine MwSt. anwendbar (Drittland)',
    category: 'non_eu'
  };
}

/**
 * Calculate price with VAT based on display config
 * @param {number} netPrice - Net price in EUR
 * @param {object} vatConfig - VAT display config from getVATDisplayConfig
 * @returns {object} Price breakdown
 */
export function calculatePriceWithVAT(netPrice, vatConfig) {
  const vatAmount = vatConfig.showVAT ? netPrice * (vatConfig.vatRate / 100) : 0;
  const grossPrice = netPrice + vatAmount;
  
  return {
    netPrice,
    vatRate: vatConfig.vatRate,
    vatAmount: Math.round(vatAmount * 100) / 100,
    grossPrice: Math.round(grossPrice * 100) / 100,
    showVAT: vatConfig.showVAT,
    isReverseCharge: vatConfig.category === 'eu_reverse_charge'
  };
}

/**
 * Format price for display
 * @param {number} amount - Amount in EUR
 * @param {string} locale - Locale for formatting (default: de-DE)
 * @returns {string} Formatted price
 */
export function formatPrice(amount, locale = 'de-DE') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Get human-readable country name
 * @param {string} countryCode - ISO 2-letter country code
 * @returns {string} Country name
 */
export const COUNTRY_NAMES = {
  // Europe
  AT: 'Austria',
  BE: 'Belgium',
  BG: 'Bulgaria',
  HR: 'Croatia',
  CY: 'Cyprus',
  CZ: 'Czech Republic',
  DK: 'Denmark',
  EE: 'Estonia',
  FI: 'Finland',
  FR: 'France',
  DE: 'Germany',
  GR: 'Greece',
  HU: 'Hungary',
  IE: 'Ireland',
  IT: 'Italy',
  LV: 'Latvia',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  MT: 'Malta',
  NL: 'Netherlands',
  PL: 'Poland',
  PT: 'Portugal',
  RO: 'Romania',
  SK: 'Slovakia',
  SI: 'Slovenia',
  ES: 'Spain',
  SE: 'Sweden',
  // Non-EU Europe
  GB: 'United Kingdom',
  CH: 'Switzerland',
  NO: 'Norway',
  // Americas
  US: 'United States',
  CA: 'Canada',
  MX: 'Mexico',
  BR: 'Brazil',
  AR: 'Argentina',
  // Asia Pacific
  JP: 'Japan',
  KR: 'South Korea',
  CN: 'China',
  IN: 'India',
  AU: 'Australia',
  NZ: 'New Zealand',
  SG: 'Singapore',
  // Middle East
  AE: 'United Arab Emirates',
  IL: 'Israel',
  SA: 'Saudi Arabia'
};

export function getCountryName(countryCode) {
  return COUNTRY_NAMES[countryCode?.toUpperCase()] || countryCode;
}
